import React, { useState, useEffect, useRef } from "react";

const Header = () => {

  const [scroll, setScroll] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const dropdownRef = useRef(null);
  const loginDropdownRef = useRef(null);
  const drawerRef = useRef(null);

  /* Header background on scroll */
  useEffect(() => {
    const handleScroll = () => setScroll(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* Lock body scroll when drawer opens */
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  /* Desktop outside click close */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth < 1024) return;

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsTermsOpen(false);
      }

      if (
        loginDropdownRef.current &&
        !loginDropdownRef.current.contains(event.target)
      ) {
        setIsLoginOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ESC key close */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        setIsTermsOpen(false);
        setIsLoginOpen(false);
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  /* Focus trap for drawer */
  useEffect(() => {
    if (!isMenuOpen) return;

    const focusable = drawerRef.current.querySelectorAll("a,button");
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTab = (e) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleTab);

    return () => document.removeEventListener("keydown", handleTab);
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsTermsOpen(false);
    setIsLoginOpen(false);
  };

  const toggleTerms = () => {
    setIsTermsOpen(!isTermsOpen);
    setIsLoginOpen(false);
  };

  const toggleLogin = () => {
    setIsLoginOpen(!isLoginOpen);
    setIsTermsOpen(false);
  };

  return (
    <>
      {/* HEADER */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scroll
            ? "bg-white/80 backdrop-blur-md border-b"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          <nav className="flex h-20 items-center justify-between">

            {/* Logo */}
            <a href="/" className="flex items-center gap-3">
              <img src="assets/img/opdcover-logo-new.png" width="126" alt="brand logo" />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden items-center gap-1 lg:flex">

              <a
                href="/"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[#F5F1EE]"
              >
                Home
              </a>

              <a
                href="/plans"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[#F5F1EE]"
              >
                Our Plans
              </a>

              {/* Terms Dropdown */}
              <div ref={dropdownRef} className="relative">

                <button
                  onClick={toggleTerms}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[#F5F1EE]"
                >
                  Terms & Policies

                  <svg
                    className={`h-4 w-4 transition-transform duration-300 ${
                      isTermsOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>

                </button>

                <div
                  className={`absolute left-0 mt-2 w-[220px] rounded-lg bg-white shadow-lg transform transition-all duration-200 origin-top ${
                    isTermsOpen
                      ? "scale-100 opacity-100"
                      : "scale-95 opacity-0 pointer-events-none"
                  }`}
                >

                  <a
                    href="/term-of-use"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Terms of Use
                  </a>

                  <a
                    href="/privacy-policy"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Privacy Policy
                  </a>

                  <a
                    href="/shipping-delivery-policy"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Shipping & Delivery Policy
                  </a>

                  <a
                    href="/cancellation-policy"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Cancellation Policy
                  </a>

                </div>

              </div>

              <a
                href="/contact"
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[#F5F1EE]"
              >
                Contact Us
              </a>

            </div>

            {/* Right Section */}
            <div className="flex items-center gap-3">

              {/* Login Dropdown */}
              <div ref={loginDropdownRef} className="relative hidden sm:block">

                <button
                  onClick={toggleLogin}
                  className="flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-lg btn-gradient"
                >
                  Login

                  <svg
                    className={`h-4 w-4 transition-transform duration-300 ${
                      isLoginOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>

                </button>

                <div
                  className={`absolute right-0 mt-2 w-[190px] rounded-lg bg-white shadow-lg transform transition-all duration-200 origin-top ${
                    isLoginOpen
                      ? "scale-100 opacity-100"
                      : "scale-95 opacity-0 pointer-events-none"
                  }`}
                >

                  <a
                    href="login.html"
                    target="_blank"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Customer Login
                  </a>

                  <a
                    href="https://portal.opdcover.com/"
                    target="_blank"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Partner / Leader Login
                  </a>

                  

                </div>

              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMenu}
                className="rounded-lg p-2.5 text-gray-600 hover:bg-[#F5F1EE] lg:hidden"
              >
                ☰
              </button>

            </div>

          </nav>
        </div>
      </header>

      {/* Overlay */}
      <div
        onClick={toggleMenu}
        className={`fixed inset-0 z-40 bg-gray-950/60 backdrop-blur-sm transition-opacity ${
          isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Mobile Drawer */}
      <div
        ref={drawerRef}
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white transform transition-transform duration-300 ease-out flex flex-col ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >

        {/* Drawer Header */}
        <div className="flex h-20 items-center justify-between px-6 border-b">

          <span className="font-display text-lg font-bold text-gray-900">
            Menu
          </span>

          <button
            onClick={toggleMenu}
            className="rounded-lg p-2 text-gray-500 hover:bg-[#F5F1EE]"
          >
            ✕
          </button>

        </div>

        {/* Scrollable Drawer Content */}
        <nav className="flex-1 overflow-y-auto overscroll-contain px-6 py-6">

          <div className="space-y-1">

            <a href="/" className="block rounded-lg px-4 py-3 hover:bg-[#F5F1EE]">
              Home
            </a>

            <a href="/plans" className="block rounded-lg px-4 py-3 hover:bg-[#F5F1EE]">
              Our Plans
            </a>

            {/* Mobile Terms */}
            <div>

              <button
                onClick={toggleTerms}
                className="flex w-full items-center justify-between rounded-lg px-4 py-3 hover:bg-[#F5F1EE]"
              >
                Terms & Policies

                <svg
                  className={`h-4 w-4 transition-transform ${
                    isTermsOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>

              </button>

              {isTermsOpen && (
                <div className="pl-6 pt-2 space-y-2">

                  <a href="/term-of-use" className="block py-2 text-sm text-gray-700">
                    Terms of Use
                  </a>

                  <a href="/privacy-policy" className="block py-2 text-sm text-gray-700">
                    Privacy Policy
                  </a>

                  <a href="/shipping-delivery-policy" className="block py-2 text-sm text-gray-700">
                    Shipping & Delivery Policy
                  </a>

                  <a href="/cancellation-policy" className="block py-2 text-sm text-gray-700">
                    Cancellation Policy
                  </a>

                </div>
              )}

            </div>

            <a href="/contact" className="block rounded-lg px-4 py-3 hover:bg-[#F5F1EE]">
              Contact Us
            </a>

          </div>

          {/* Drawer Buttons */}
          <div className="mt-8 space-y-3">

            <a
              href="login.html"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl btn-gradient px-8 py-4 font-semibold text-white cursor-pointer transition-all"
            >
              Customer Login
            </a>

            <a
              href="https://portal.opdcover.com/"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl btn-gradient px-8 py-4 font-semibold text-white cursor-pointer transition-all"
            >
              Agent / Leader Login
            </a>

          </div>

        </nav>
      </div>
    </>
  );
};

export default Header;